import React, { createContext, useContext, useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

const UserContext = createContext(null);

const USER_STORAGE_KEY = 'ttt_user_data';
const USER_NAME_KEY = 'ttt_user_name';
const USER_AVATAR_KEY = 'ttt_user_avatar';
const USER_BORDER_KEY = 'ttt_user_border';
let sessionStartFired = false;

export function UserProvider({ children }) {
  const authContext = useAuth();
  const authUser = authContext?.user;
  
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // User's display name (set during onboarding)
  const [userName, setUserNameState] = useState(() => {
    return localStorage.getItem(USER_NAME_KEY) || '';
  });

  // User's avatar (base64 image)
  const [userAvatar, setUserAvatarState] = useState(() => {
    return localStorage.getItem(USER_AVATAR_KEY) || '';
  });

  // User's profile border color
  const [userBorder, setUserBorderState] = useState(() => {
    return localStorage.getItem(USER_BORDER_KEY) || '#9DDBB9';
  });

  useEffect(() => {
    // Sync with Supabase auth user if available
    if (authUser) {
      const syncedUser = {
        id: authUser.id,
        email: authUser.email,
        loginMethod: 'supabase_auth',
        ...authUser.user_metadata
      };
      setUser(syncedUser);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(syncedUser));
      
      // Identify user in PostHog
      if (posthog) {
        posthog.identify(authUser.id, {
          email: authUser.email,
          name: authUser.user_metadata?.name || userName || 'User',
          returning_user: true,
          login_method: 'supabase_auth',
        });
        
        if (!sessionStartFired) {
          sessionStartFired = true;
          posthog.capture('user_session_start', {
            user_email: authUser.email,
            user_name: authUser.user_metadata?.name || userName,
            returning_user: true,
          });
        }
      }
      
      // Check for subscription status from Supabase
      const checkSubscription = async () => {
        try {
          const { data } = await supabase
            .from('subscriptions')
            .select('status, current_period_end')
            .eq('user_email', authUser.email)
            .maybeSingle();

          const validStatuses = ['active', 'trialing', 'subscribed'];
          if (data && validStatuses.includes(data.status) && new Date(data.current_period_end) > new Date()) {
            localStorage.setItem('ttt_has_plan', 'premium');
          } else {
            localStorage.removeItem('ttt_has_plan');
          }
        } catch (err) {
          console.error('Error checking subscription:', err);
        }
      };
      
      checkSubscription();
      setIsLoading(false);
      return;
    }
    
    // Fall back to localStorage-based user (legacy)
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        
        // Identify user in PostHog
        const userEmail = userData.email || userData.id;
        const userName = localStorage.getItem(USER_NAME_KEY);
        
        if (userEmail && posthog) {
          posthog.identify(userData.id || userEmail, {
            email: userEmail,
            name: userName || 'User',
            returning_user: true,
            login_method: userData.loginMethod || 'quick_login',
          });
          
          // Track session start
          if (!sessionStartFired) {
            sessionStartFired = true;
            posthog.capture('user_session_start', {
              user_email: userEmail,
              user_name: userName,
              returning_user: true,
            });
          }
        }
      } catch (e) {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, [authUser, userName]);

  // Persist userName to localStorage
  useEffect(() => {
    if (userName) {
      localStorage.setItem(USER_NAME_KEY, userName);
    }
  }, [userName]);

  // Persist userAvatar to localStorage
  useEffect(() => {
    if (userAvatar) {
      localStorage.setItem(USER_AVATAR_KEY, userAvatar);
    } else {
      localStorage.removeItem(USER_AVATAR_KEY);
    }
  }, [userAvatar]);

  // Persist userBorder to localStorage
  useEffect(() => {
    localStorage.setItem(USER_BORDER_KEY, userBorder);
  }, [userBorder]);

  const login = (userData) => {
    setUser(userData);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(USER_STORAGE_KEY);
  };

  // Set user name (from onboarding)
  const setUserName = (name) => {
    const trimmedName = name?.trim() || '';
    setUserNameState(trimmedName);
    // Also update in user object if logged in
    if (user) {
      const updatedUser = { ...user, displayName: trimmedName };
      setUser(updatedUser);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
    }
  };

  // Set user avatar (base64 image)
  const setUserAvatar = (avatarBase64) => {
    setUserAvatarState(avatarBase64 || '');
  };

  // Set user border color
  const setUserBorder = (borderColor) => {
    setUserBorderState(borderColor || '#9DDBB9');
  };

  const isLoggedIn = !!user;

  return (
    <UserContext.Provider value={{ 
      user, 
      isLoading, 
      isLoggedIn, 
      login, 
      logout,
      userName,
      setUserName,
      userAvatar,
      setUserAvatar,
      userBorder,
      setUserBorder
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
